/**
 * ImpersonationBanner — toont een oranje banner bovenaan de app als de
 * huidige sessie een impersonated user is (superadmin logt in als andere user).
 *
 * - "Stop impersonation" → server geeft nieuwe tokens voor origineel,
 *   geen login-flow nodig.
 * - "Switch user" dropdown → snel switchen naar andere test-user zonder
 *   uit en in te moeten loggen. Backend houdt de chain op originele
 *   superadmin via impersonatedBy claim.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, LogOut, ChevronDown, Search } from 'lucide-react';
import { useAuth } from '../auth/AuthContext.js';

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return null;
    const padded = payloadB64.padEnd(payloadB64.length + (4 - (payloadB64.length % 4)) % 4, '=');
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

interface UserListItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  entity: { id: string; name: string } | null;
}

export function ImpersonationBanner() {
  const { user, impersonate, stopImpersonate } = useAuth();
  const [stopping, setStopping] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<UserListItem[] | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const impersonationInfo = useMemo(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    const payload = decodeJwt(token);
    if (!payload?.impersonatedBy) return null;
    return {
      impersonatedByEmail: payload.impersonatedByEmail as string | undefined,
    };
  }, [user?.id]);

  // Lazy fetch user list pas bij dropdown-open
  useEffect(() => {
    if (!menuOpen || users) return;
    const token = localStorage.getItem('accessToken');
    fetch('/api/auth/users-list', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then((list: UserListItem[]) => setUsers(list))
      .catch(() => setUsers([]));
  }, [menuOpen, users]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  if (!impersonationInfo) return null;

  async function handleStop() {
    if (stopping) return;
    setStopping(true);
    try {
      await stopImpersonate();
      // hard navigate gebeurt in stopImpersonate
    } catch (e: any) {
      alert(`Stoppen mislukt: ${e?.message || 'onbekende fout'}`);
      setStopping(false);
    }
  }

  async function handleSwitch(userId: string) {
    if (switching) return;
    setSwitching(userId);
    try {
      await impersonate(userId);
    } catch (e: any) {
      alert(`Switchen mislukt: ${e?.message || 'onbekende fout'}`);
      setSwitching(null);
    }
  }

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = search.toLowerCase().trim();
    if (!q) return users;
    return users.filter(u =>
      u.email.toLowerCase().includes(q) ||
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      (u.entity?.name || '').toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q),
    );
  }, [users, search]);

  return (
    <div className="sticky top-0 z-[100] bg-orange-500 text-white px-4 py-2 flex items-center justify-between shadow-md">
      <div className="flex items-center gap-2 text-sm font-medium min-w-0">
        <AlertTriangle size={16} className="flex-shrink-0" />
        <span className="truncate">
          Je bent ingelogd als <strong>{user?.firstName} {user?.lastName}</strong>
          {' '}({user?.email}, rol: {user?.role})
          {impersonationInfo.impersonatedByEmail && (
            <span className="opacity-90"> — door {impersonationInfo.impersonatedByEmail}</span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Switch user dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
          >
            Switch user <ChevronDown size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-80 bg-white rounded-xl shadow-lg border border-orange-200 text-gray-900 max-h-96 overflow-hidden flex flex-col">
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Zoek user, winkel, rol..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-200"
                  />
                </div>
              </div>
              <div className="overflow-y-auto flex-1">
                {!users && (
                  <div className="px-3 py-4 text-xs text-gray-400 text-center">Laden...</div>
                )}
                {users && filtered.length === 0 && (
                  <div className="px-3 py-4 text-xs text-gray-400 text-center">Geen resultaten</div>
                )}
                {filtered.map(u => (
                  <button
                    key={u.id}
                    onClick={() => handleSwitch(u.id)}
                    disabled={!!switching}
                    className="w-full text-left px-3 py-2 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed border-b border-gray-50 last:border-0"
                  >
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {u.firstName} {u.lastName}
                      {switching === u.id && <span className="text-xs text-orange-600 ml-2">switchen...</span>}
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {u.email} · <span className="font-mono">{u.role}</span>
                      {u.entity && <> · {u.entity.name}</>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Stop impersonation */}
        <button
          onClick={handleStop}
          disabled={stopping}
          className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
        >
          <LogOut size={14} />
          {stopping ? 'Bezig...' : 'Terug naar mezelf'}
        </button>
      </div>
    </div>
  );
}
